// Copyright © SRGTech (Software Resonance Group Inc.). All rights reserved.
// SPDX-License-Identifier: LicenseRef-SRGTech-Proprietary
// See LICENSE and docs/legal/PROPRIETARY_COPYRIGHT.md.

/**
 * Accessible table for administration collections.
 *
 * `docs/10-TESTING.md` §30 requires semantic roles, labels, and names. A native
 * `<table>` with a caption and scoped headers gives screen-reader users row and
 * column context that a `<div>` grid does not, so the primitive is provided
 * once here rather than re-derived per screen.
 */

import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Typography from '@mui/material/Typography';
import { visuallyHidden } from '@mui/utils';
import type { ReactNode } from 'react';

/** Definition of one table column. */
export interface DataTableColumn<TRow> {
  /** Stable column key, unique within the table. */
  readonly key: string;
  /** Visible column header text. */
  readonly header: string;
  /** Renders the cell for a row. */
  readonly render: (row: TRow) => ReactNode;
  /** Numeric alignment hint. */
  readonly numeric?: boolean;
}

/** Props for {@link DataTable}. */
export interface DataTableProps<TRow> {
  /** Caption describing the table for assistive technology. */
  readonly caption: string;
  /** Whether the caption is also shown visually. */
  readonly captionVisible?: boolean;
  /** Column definitions in display order. */
  readonly columns: readonly DataTableColumn<TRow>[];
  /** Rows to render. */
  readonly rows: readonly TRow[];
  /** Returns a stable React key for a row. */
  readonly rowKey: (row: TRow) => string;
}

/** Render a collection as an accessible table. */
export function DataTable<TRow>({
  caption,
  captionVisible = false,
  columns,
  rows,
  rowKey,
}: DataTableProps<TRow>): ReactNode {
  return (
    <TableContainer>
      <Table size="small">
        <caption style={captionVisible ? undefined : visuallyHidden}>
          <Typography variant="body2" component="span">
            {caption}
          </Typography>
        </caption>
        <TableHead>
          <TableRow>
            {columns.map((column) => (
              <TableCell
                key={column.key}
                scope="col"
                component="th"
                align={column.numeric === true ? 'right' : 'left'}
              >
                {column.header}
              </TableCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          {rows.map((row) => (
            <TableRow key={rowKey(row)} hover>
              {columns.map((column) => (
                <TableCell key={column.key} align={column.numeric === true ? 'right' : 'left'}>
                  {column.render(row)}
                </TableCell>
              ))}
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
}
